/**
 * Which education levels an applicant may choose (plan D49/D60).
 *
 * <p>The backend refuses a level some authority covers but does not confirm, so
 * offering it here would be a form that submits and then fails. Pure and
 * separate because it is the part that can be wrong.
 */

export interface EducationLevelOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface EducationLevelGate {
  /** Levels some authority speaks for; everything else is free to declare. */
  restrictedLevels?: string[];
  /** What the authorities hold for this person, if anything. */
  verifiedLevel?: string;
}

/**
 * Disable every restricted level except the one the authorities confirm.
 *
 * <p>Note what happens when {@link EducationLevelGate.verifiedLevel} is absent:
 * every restricted level is disabled. That is correct and is the case the whole
 * control exists for — a person with no verified record may not assert one —
 * and it is why the gate takes the covered *set* rather than a locked flag.
 *
 * <p>Unrestricted levels are never disabled. School levels sit there today,
 * because nothing verifies them and refusing would block people over a gap that
 * is ours rather than theirs.
 */
export function applyEducationLevelGate(
  options: EducationLevelOption[],
  gate: EducationLevelGate | null | undefined,
): EducationLevelOption[] {
  const restricted = gate?.restrictedLevels;
  if (!restricted || restricted.length === 0) {
    return options;
  }
  const allowed = gate?.verifiedLevel;
  return options.map((option) =>
    restricted.includes(option.value) && option.value !== allowed
      ? { ...option, disabled: true }
      : option,
  );
}

/**
 * Whether the applicant's currently selected level is one they may submit.
 *
 * <p>Used to explain the disabled options rather than to block the submit: the
 * server is the gate, and a form that silently refuses to submit is worse than
 * one that says why.
 */
export function isEducationLevelSubmittable(
  level: string | undefined,
  gate: EducationLevelGate | null | undefined,
): boolean {
  if (!level) return true;
  const restricted = gate?.restrictedLevels;
  if (!restricted || !restricted.includes(level)) return true;
  return level === gate?.verifiedLevel;
}
